import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRightIcon } from "lucide-react";
import { TreatedImage } from "./TreatedImage";
import { BODY, EYEBROW, PANEL_HEADING } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
const PORTRAIT_URL = "https://cdn.magicpatterns.com/uploads/vocf1y2AQQYxjkG8kUjPXn/face.jpg";
const RESUME_URL = "https://cdn.magicpatterns.com/uploads/5EmUHiPAb2KjzbrHBdvvKz/Alyssa-Retza-Resume.pdf";
const rows = [{
  label: "Currently",
  value: "Director of Digital Strategy & Web Product, Lawrence University"
}, {
  label: "What I do",
  value: "I make complex digital systems make more sense, while asking what all this progress actually means for the people living with it."
}];

/** Intro: the portrait sits on the cream page as its own print and overlaps the separate espresso box. */
export function AboutSection() {
  const reduce = useReducedMotion();
  const inView = {
    once: true,
    amount: 0.3
  } as const;
  return <section id="about" aria-labelledby="intro-heading" className="w-full scroll-mt-28 bg-cream px-6 pb-24 md:scroll-mt-36 md:px-12 md:pb-32 lg:px-20 lg:pb-40" data-id="element-19" data-mp="sr1fl1t8g7q6">
      <div className="grid grid-cols-1 lg:mx-auto lg:w-[88%] lg:max-w-[1280px] lg:grid-cols-12 lg:items-center" data-id="element-20" data-mp="iuocjdbsshir">
        <motion.div className="relative z-10 -mb-20 mx-auto w-[70%] max-w-[18rem] -rotate-2 rounded-3xl shadow-[0_24px_48px_-16px_rgba(15,12,10,0.45)] md:-mb-24 md:max-w-xs lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:mb-0 lg:ml-0 lg:w-full lg:max-w-none" initial={reduce ? false : {
        opacity: 0,
        y: 16,
        rotate: -4
      }} whileInView={{
        opacity: 1,
        y: 0,
        rotate: -2
      }} viewport={inView} transition={{
        duration: 0.3,
        ease,
        delay: 0.12
      }} data-id="element-21" data-mp="nu18qook70y0">
          <TreatedImage src={PORTRAIT_URL} alt="Alyssa Retza" className="aspect-[4/5] w-full rounded-3xl bg-bark" data-id="element-22" data-mp="3n71z53lg26qt" />
        </motion.div>

        <motion.div className="rounded-3xl bg-espresso px-8 pb-10 pt-28 md:px-14 md:pb-16 md:pt-36 lg:col-span-8 lg:col-start-5 lg:row-start-1 lg:py-20 lg:pl-32 lg:pr-20 xl:pl-36" initial={reduce ? false : {
        opacity: 0,
        y: 24,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={inView} transition={{
        duration: 0.3,
        ease
      }} data-id="element-23" data-mp="3rb19t5k1mbc9">
          <h2 id="intro-heading" aria-label="Hi, I’m Alyssa." className={`${PANEL_HEADING} text-cream`} data-id="element-24" data-mp="1j00lbml5h099">
            <span aria-hidden="true" className="-my-[0.06em] block overflow-hidden py-[0.06em]" data-id="element-25" data-mp="1ey57xjpy2wf4">
              <motion.span className="block" initial={reduce ? false : {
              y: "110%"
            }} whileInView={{
              y: "0%"
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              ease,
              delay: 0.15
            }} data-id="element-26" data-mp="1a3hqg4tcz2a1">
                Hi, I’m Alyssa<span className="text-mustard" data-id="element-27" data-mp="2ze8ri6lm4wyq">.</span>
              </motion.span>
            </span>
          </h2>

          <dl className="mt-12 space-y-8 md:mt-14 md:space-y-10" data-id="element-28" data-mp="32y8y65h3png3">
            {rows.map(row => <div key={row.label} data-id="element-29" data-mp="36zugw7gbgvy7">
                <dt className={`${EYEBROW} text-mustard`} data-id="element-30" data-mp="36ysy1gt2755x">{row.label}</dt>
                <dd className={`mt-2 max-w-2xl ${BODY} text-cream`} data-id="element-31" data-mp="3bq2js6bhlocj">{row.value}</dd>
              </div>)}
          </dl>

          <div className="mt-12 flex flex-wrap gap-3 md:mt-14" data-id="element-32" data-mp="3gocgwmgruz0s">
            <a href={RESUME_URL} target="_blank" rel="noopener noreferrer" aria-label="View my résumé (PDF, opens in a new tab)" className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-rust px-6 text-sm font-medium uppercase text-cream transition-colors duration-150 ease-out hover:bg-mustard hover:text-espresso" data-id="element-33" data-mp="2jzwp0jq6wdki">
              View my résumé
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-34" data-mp="89c758legd1v" />
            </a>
            <a href="#contact" className="flex h-12 items-center whitespace-nowrap rounded-full px-6 text-sm font-medium uppercase text-cream ring-1 ring-cream/20 transition-colors duration-150 ease-out hover:bg-cream hover:text-espresso" data-id="element-35" data-mp="d7ngli7s8ljk">
              Let’s talk
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}